/**
 * InlineConcierge — MS59 embeddable conversation surface.
 *
 * The chat experience from /chat, rendered inline within a parent
 * container (today: the landing hero in VariantB). Lets the user
 * have a full chat-concierge conversation — including vendor
 * recommendations and the order-confirm sheet — without ever
 * leaving the landing page.
 *
 * Why this is the truer Sprint 1.4 design:
 *   The original SmartIntentBar comment captures Krishna's principle:
 *   "chat-vs-browse mode-choice collapsed into one input. The system
 *   disambiguates instead of the user." The current routing-to-/chat
 *   defers the mode-choice into a page transition the user has to
 *   live with. Inlining the chat into the hero means the user
 *   genuinely never makes the chat-vs-browse decision — the bar just
 *   morphs into a chat surface when they typed something chat-like,
 *   and the browse escape hatch (cycling categories, "or browse all"
 *   trailer, nav menu) all stay visible in the surrounding hero.
 *
 * Architecture:
 *   - useInlineConversation owns sessionId + turns + chips + status +
 *     the chat-concierge round-trip. This component is the
 *     presentation layer over that engine.
 *   - Reuses the same leaf components /chat uses (ChatBubble,
 *     ThinkingBubble, ComposerInput, SkuCard, OrderConfirmSheet,
 *     VendorGridOverlay) so the chat experience feels identical
 *     across surfaces — buyer doesn't have to learn two grammars.
 *   - No h-screen / sticky-bottom assumptions. The container parent
 *     controls layout; we render a natural-flow block that grows as
 *     turns accumulate.
 *
 * Differences vs /chat:
 *   - No `RecipientContextHeader`. The landing hero already carries
 *     the editorial framing (H1, cycling categories, lede); a chat-
 *     style "Sarah · birthday" bar would compete with it. The
 *     recipient context is woven into the empty-state copy and the
 *     ThinkingStrip's personalised verb cycle instead.
 *   - "Back to start" button replaces /chat's deeper restart-flow
 *     (no URL params to clear).
 *   - Smaller max-width (640px vs /chat's 768px) so the thread sits
 *     comfortably inside the hero's 820px column.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ChatBubble } from "@/components/chat/ChatBubble";
import { ThreadWidgetStrip } from "@/components/concierge/ThreadWidgetStrip";
// MS203: ThinkingBubble replaces ChatTypingIndicator's three-dot
// pulse with the alive-mascot icon + rotating verb pattern,
// rendered in the same chat-bubble chrome so the visual transition
// to a real assistant message is seamless.
import { ThinkingBubble } from "@/components/chat/ThinkingBubble";
import { ComposerInput } from "@/components/chat/ComposerInput";
import { VendorGridOverlay } from "@/components/chat/VendorGridOverlay";
import type { SkuCardData } from "@/components/chat/SkuCard";
import { ProductCheckoutModals } from "@/components/product-detail/ProductCheckoutModals";
import { useAuth } from "@/hooks/useAuth";
import {
  useProductCheckoutFlow,
  type ProductCheckoutCameFrom,
} from "@/hooks/useProductCheckoutFlow";
import { getOrCreateGuestSessionId } from "@/lib/guestSession";
import { captureInteraction } from "@/lib/captureInteraction";
import {
  useInlineConversation,
  type InlineConversationBrief,
} from "@/hooks/useInlineConversation";
import type { DashboardClientActionHandlers } from "@/lib/dashboardClientActions";
import type { TeamClientActionHandlers } from "@/lib/teamClientActions";

// MS271: starter-chip suggestions shown when the thread is empty.
// The FloatingConcierge (MS264) opens with an empty `raw_text` brief
// so the user sees an empty composer with no hint about the chat's
// expanded capabilities (MS265–267 added skip / update / create
// celebration tools). Without these chips users either type
// generic "hi" or assume the chat only does product recommendations
// — invisible features.
//
// Each chip is a complete user message; the chat-concierge prompt
// can ask the follow-up ("which celebration?" etc) so these stay
// short and high-signal. Order is intentional: "what's coming up"
// is the read-only safe entry; the mutate-style intents come next.
const STARTER_CHIPS = [
  "What's coming up?",
  "Skip a celebration",
  "Update someone's preferences",
  "Send a gift",
] as const;

interface InlineConciergeProps {
  /** The brief the SmartIntentBar classified. The hook auto-sends
   *  `raw_text` as the first user message on mount. */
  initialBrief: InlineConversationBrief;
  /** Auto-send `initialBrief.raw_text` as the first message on mount.
   *  Defaults to true; FloatingConcierge passes false when opened without
   *  a deep-link brief so it doesn't fire an empty first message. */
  autoSendFirstMessage?: boolean;
  /** Called when the user taps "Back to start" — typically the
   *  parent (VariantB) restores the SmartIntentBar idle state. */
  onReset: () => void;
  /** MS573: optional active-draft scope. When set (DashboardConcierge
   *  Drawer wires this), the hook stamps `chat_sessions.is_active_draft
   *  = true` after the first turn and clears it on reset. Other surfaces
   *  (landing hero, FloatingConcierge) omit this prop and the
   *  flag-write lifecycle is byte-identical to pre-MS573. */
  activeDraftScope?: {
    user_id: string;
    recipient_id: string;
    celebration_id?: string;
  } | null;
  /** MS573: optional scope-based active-draft restore. When set and no
   *  existing scope-matching draft exists, the surface opens with a
   *  fresh empty session. When a draft does exist, the hook restores
   *  it (turns + chips + sessionId) before the user types anything. */
  loadActiveDraftFor?: {
    user_id: string;
    recipient_id: string;
  } | null;
  /** Telemetry + modal attribution. Landing hero defaults to "landing";
   *  dashboard drawer passes "dashboard". */
  cameFrom?: ProductCheckoutCameFrom;
  /** Linked celebration when opened from dashboard adjust flow. */
  celebrationId?: string | null;
  /** When the assistant surfaces recommendation cards, parent shells
   *  (e.g. FloatingConcierge) can auto-expand the panel. */
  onRecommendationCardsCount?: (count: number) => void;
  /** Phase 2g — Home dashboard UI tool callbacks. */
  onDashboardClientActions?: DashboardClientActionHandlers | null;
  /** Team page — highlight row / open profile from thread widgets. */
  onTeamClientActions?: TeamClientActionHandlers | null;
  chatSurface?: "dashboard" | "chat" | "landing" | "floating" | string;
  /** Dedicated sessionStorage key (floating widget vs landing hero). */
  sessionPersistKey?: string;
  /** Floating messenger shell: no duplicate header, flex thread. */
  variant?: "default" | "floating" | "dashboard";
  /** When false, Start over clears the thread but does not call onReset. */
  resetClosesSurface?: boolean;
  /** Parent can wire shell chrome (e.g. floating header Start over). */
  shellResetRef?: React.MutableRefObject<(() => void) | null>;
  /** Linked celebration checkout prefill (dashboard / approval adjust). */
  checkoutDefaultDate?: string;
  checkoutDefaultAddress?: string;
  /** Restore initial_brief from session for header / prefill rehydration. */
  includeInitialBriefRestore?: boolean;
  /** Fired when chat-concierge returns resolved_brief (floating context pin). */
  onResolvedBrief?: (brief: unknown) => void;
}

export function InlineConcierge({
  initialBrief,
  onReset,
  activeDraftScope = null,
  loadActiveDraftFor = null,
  cameFrom = "landing",
  celebrationId = null,
  onRecommendationCardsCount,
  onDashboardClientActions = null,
  onTeamClientActions = null,
  chatSurface,
  sessionPersistKey,
  variant = "default",
  resetClosesSurface = true,
  shellResetRef,
  checkoutDefaultDate,
  checkoutDefaultAddress,
  includeInitialBriefRestore = false,
  onResolvedBrief,
  autoSendFirstMessage = true,
}: InlineConciergeProps) {
  const isFloating = variant === "floating";
  const isDashboard = variant === "dashboard";
  const isMessenger = isFloating || isDashboard;
  const {
    sessionId,
    turns,
    chips,
    status,
    errorMessage,
    retry,
    costSnapshot,
    submit,
    submitChip,
    submitWidgetAction,
    reset,
    // MS598: the in-progress streaming assistant turn (flag-gated; null
    // when CHAT_STREAMING_ENABLED is OFF, so the render branch below is
    // unreachable in production and this surface stays byte-identical).
    streamingTurn,
    // MS294: gate the empty-state starter chips on this flag so they
    // don't flash before a session restore (MS288) has resolved.
    isRestoring,
    latestResolvedBrief,
    isInFlight,
    stopGeneration,
    editLastUserMessage,
    lastUserTurnId,
    composerPrefillKey,
    composerPrefill,
    workingBriefSummary,
  } = useInlineConversation({
    initialBrief,
    autoSendFirstMessage,
    captureSurface: cameFrom === "dashboard" ? "dashboard" : "landing",
    chatSurface:
      chatSurface ?? (cameFrom === "dashboard" ? "dashboard" : isFloating ? "floating" : undefined),
    onDashboardClientActions,
    onTeamClientActions,
    sessionPersistKey,
    // MS573: when the drawer wires draft persistence, these flow
    // through to the hook. Other surfaces pass null/undefined and
    // the hook's draft lifecycle is a no-op.
    activeDraftScope,
    loadActiveDraftFor,
    includeInitialBriefRestore,
    contextCelebrationId: celebrationId ?? initialBrief.celebration_id ?? null,
  });

  const { session, loading: authLoading } = useAuth();
  const isAnon = !authLoading && !session;
  const guestSessionId = useMemo(
    () => (isAnon ? getOrCreateGuestSessionId() : null),
    [isAnon],
  );

  const checkout = useProductCheckoutFlow({
    cameFrom,
    onViewCard: (sku) => {
      void captureInteraction({
        surface: cameFrom === "dashboard" ? "dashboard" : "landing",
        event_type: "card_selected",
        payload: { session_id: sessionId, sku_id: sku.sku_id, sku_slug: sku.slug },
        recipient_id: initialBrief.recipient_id ?? null,
        vendor_product_id: sku.sku_id,
      });
    },
  });

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlaySkus, setOverlaySkus] = useState<SkuCardData[]>([]);

  // Auto-scroll: messenger pins tail inside the message list scroll
  // container (Intercom-style — only scroll when user is at bottom).
  // Landing/default uses scrollIntoView on the thread anchor.
  const threadEndRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = useRef(true);
  const SCROLL_BOTTOM_THRESHOLD_PX = 64;

  const scrollMessengerToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = messageListRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const handleMessageListScroll = useCallback(() => {
    const el = messageListRef.current;
    if (!el) return;
    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedToBottomRef.current =
      distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
  }, []);

  useEffect(() => {
    if (isMessenger) {
      if (pinnedToBottomRef.current) {
        scrollMessengerToBottom("smooth");
      }
      return;
    }
    threadEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
    // MS598: streamingTurn.text grows as deltas land; keep the tail pinned
    // so the streaming prose stays in view. Inert when the flag is OFF
    // (streamingTurn stays null → identity never changes → no extra runs).
  }, [turns, status, streamingTurn, isMessenger, scrollMessengerToBottom]);

  // Notify parent shells when recommendation cards are visible so
  // FloatingConcierge can auto-expand for carousel readability.
  useEffect(() => {
    if (!onRecommendationCardsCount) return;
    const lastWithCards = [...turns]
      .reverse()
      .find((t) => t.role === "assistant" && t.cards && t.cards.length > 0);
    onRecommendationCardsCount(lastWithCards?.cards?.length ?? 0);
  }, [turns, onRecommendationCardsCount]);

  const handleSeeAll = (cardsForOverlay: SkuCardData[]) => {
    void captureInteraction({
      surface: "landing",
      event_type: "see_all_opened",
      payload: { session_id: sessionId, candidate_count: cardsForOverlay.length },
      recipient_id: initialBrief.recipient_id ?? null,
    });
    setOverlaySkus(cardsForOverlay);
    setOverlayOpen(true);
  };

  const handleResetClick = useCallback(() => {
    reset();
    if (resetClosesSurface) onReset();
  }, [reset, resetClosesSurface, onReset]);

  useEffect(() => {
    if (!shellResetRef) return;
    shellResetRef.current = handleResetClick;
    return () => {
      shellResetRef.current = null;
    };
  }, [shellResetRef, handleResetClick]);

  useEffect(() => {
    if (latestResolvedBrief == null) return;
    onResolvedBrief?.(latestResolvedBrief);
  }, [latestResolvedBrief, onResolvedBrief]);

  const composerSection = (
    <div
      data-testid={isMessenger ? "concierge-composer" : undefined}
      className={`shrink-0 border-t border-charcoal/8 pt-3 ${
        isMessenger ? "px-3 pb-3" : "mt-4 pt-4"
      }`}
    >
      {isMessenger &&
        turns.length === 0 &&
        status === "ready" &&
        !isRestoring && (
          <div
            className="mb-3 flex flex-col gap-2"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            <p className="text-[13px] text-navy/75">
              Ask me anything — gift ideas, who's celebrating, or changes to
              your team.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STARTER_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => void submit(chip)}
                  className="rounded-full border border-navy/15 bg-white px-3 py-1 text-xs text-navy/85 transition-colors hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

      {chips.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {chips.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => void submitChip(c)}
              disabled={isInFlight || status === "circuit_broken"}
              className="rounded-full border border-navy/20 px-3 py-1 text-sm text-navy hover:border-coral hover:text-coral disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-coral"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <ComposerInput
        onSubmit={(text) => void submit(text)}
        disabled={isInFlight || status === "circuit_broken"}
        showStop={isInFlight}
        onStop={stopGeneration}
        prefillKey={composerPrefillKey}
        prefill={composerPrefill}
      />

      {costSnapshot?.near_circuit_breaker && (
        <p
          className="mt-2 text-[11px] text-charcoal/55"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          Heads up: this chat is getting long. We may suggest starting fresh
          soon.
        </p>
      )}
    </div>
  );

  // Explicit lead-in when the assistant pre-loader / stream follows a user
  // turn — flex `gap` alone was too easy to miss on the landing hero
  // (pre-MS745 the inner thread had no gap at all).
  const assistantRowLeadInClass =
    turns.at(-1)?.role === "user" ? (isMessenger ? "pt-4" : "pt-6") : "";

  const messageThread = (
    <>
      {!isMessenger &&
        turns.length === 0 &&
        status === "ready" &&
        !isRestoring && (
          <div
            className="flex flex-col gap-3 rounded-2xl bg-cream/55 px-4 py-3.5"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            <p className="text-[13px] text-navy/75">
              Ask me anything — gift ideas, who's celebrating, or changes to
              your team.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STARTER_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => void submit(chip)}
                  className="rounded-full border border-navy/15 bg-white px-3 py-1 text-xs text-navy/85 transition-colors hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

      <div
        className={
          isMessenger
            ? "flex flex-col gap-4 px-3 py-3"
            : "flex flex-col gap-6"
        }
        data-testid="concierge-thread-turns"
      >
          {!isMessenger && workingBriefSummary.length > 0 && (
            <div
              className="rounded-xl border border-navy/10 bg-butter/25 px-3.5 py-2.5"
              data-testid="concierge-working-brief-strip"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-charcoal/55">
                What we understood
              </p>
              <ul className="mt-1.5 flex flex-col gap-0.5 text-[13px] text-navy/80">
                {workingBriefSummary.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {turns.map((t) => (
            <div key={t.id} className="flex flex-col gap-2">
              <ChatBubble
                role={t.role}
                text={t.text}
                cards={t.cards}
                totalCandidates={t.cards?.length}
                onView={checkout.handleViewCard}
                onSeeAll={t.cards ? () => handleSeeAll(t.cards!) : undefined}
                animate={t.animate}
                onEdit={
                  t.role === "user" &&
                  t.id === lastUserTurnId &&
                  !isRestoring &&
                  status !== "circuit_broken"
                    ? () => editLastUserMessage()
                    : undefined
                }
              />
              {t.role === "assistant" &&
                t.threadWidgets &&
                t.threadWidgets.length > 0 && (
                  <ThreadWidgetStrip
                    widgets={t.threadWidgets}
                    onWidgetAction={(action) => void submitWidgetAction(action)}
                    onSendMessage={(msg) => void submit(msg)}
                    disabled={isInFlight || status === "circuit_broken"}
                  />
                )}
            </div>
          ))}

          {/* Pre-loader. MS598: shown ONLY during "thinking" (before the
              first delta). The moment a delta lands the hook flips status
              to "streaming" and populates streamingTurn — the pre-loader
              unmounts and the growing bubble below mounts in the same slot,
              a morph handoff rather than a hard swap. With the flag OFF,
              status never becomes "streaming", so this behaves exactly as
              pre-MS598 (pre-loader during "thinking", whole turn at done). */}
          {status === "thinking" && (
            <div
              className={`flex w-full justify-start ${assistantRowLeadInClass}`}
              data-testid="concierge-thinking-row"
            >
              <div className="flex w-full max-w-[min(680px,92%)] flex-col items-start gap-3 min-w-0">
                <p
                  className="ml-1 text-xs uppercase tracking-wider text-charcoal/55"
                  style={{ fontFamily: "DM Sans, sans-serif", letterSpacing: "0.08em" }}
                >
                  Lembra
                </p>
                <ThinkingBubble
                  recipientFirstName={
                    initialBrief.recipient_name?.split(/\s+/)[0] ?? null
                  }
                />
              </div>
            </div>
          )}

          {/* MS598: the in-progress streaming bubble. Reuses the SAME
              ChatBubble the finalized turn uses (same chrome, same carousel)
              so the handoff to the persisted turn at `done` is seamless —
              the bubble just stops growing and re-renders its prose as
              markdown. `verb-fade` gives it the same gentle entrance the
              thinking-strip verbs use, so replacing the pre-loader reads as
              a cross-fade. Null (unreachable) when the flag is OFF. */}
          {streamingTurn && (
            <div
              className={`flex justify-start verb-fade ${assistantRowLeadInClass}`}
            >
              <ChatBubble
                role="assistant"
                text={streamingTurn.text}
                cards={streamingTurn.cards}
                totalCandidates={streamingTurn.cards?.length}
                onView={checkout.handleViewCard}
                onSeeAll={
                  streamingTurn.cards
                    ? () => handleSeeAll(streamingTurn.cards!)
                    : undefined
                }
                streaming
              />
            </div>
          )}

          {status === "error" && errorMessage && (
            <div
              className="flex flex-col gap-2 rounded-2xl border border-coral/40 bg-coral/5 px-4 py-3 text-sm text-coral"
              style={{ fontFamily: "DM Sans, sans-serif" }}
              role="alert"
            >
              <p>{errorMessage}</p>
              {/* MS204: explicit retry button so the buyer doesn't
                  have to retype the failed message. retry() walks
                  back to the last user turn in `turns`, drops it,
                  and re-fires submit() with the same text. */}
              <button
                type="button"
                onClick={() => void retry()}
                className="self-start rounded-full bg-coral px-4 py-1.5 text-xs font-semibold text-white hover:bg-coral/85 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
              >
                Try again
              </button>
            </div>
          )}

          {status === "circuit_broken" && (
            <div
              className="flex flex-col gap-3 rounded-2xl border border-charcoal/15 bg-white px-4 py-4 text-sm text-navy"
              style={{ fontFamily: "DM Sans, sans-serif" }}
              role="alert"
            >
              <p>{errorMessage ?? "We hit a snag mid-thought, let's start over."}</p>
              <button
                type="button"
                onClick={handleResetClick}
                className="self-start rounded-full bg-coral px-4 py-2 text-sm font-semibold text-white hover:bg-coral/85 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
              >
                Start a new chat
              </button>
            </div>
          )}

          {/* MS287: was a passive "Brief locked in. Tap any card above
              to send." banner. Lucky's 2026-05-18 audit: that banner
              is a dead-end — when the user explicitly says "Send this
              one" the chat would lock the conversation and tell them
              to scroll up + tap a card, which then opens a detail
              modal, not the order sheet. Two extra clicks for what
              should be one obvious action.

              Now: when the assistant's last turn has cards, render the
              banner as an ACTIVE primary button. One tap opens the
              OrderConfirmSheet directly for the most-relevant card
              (the only card if there's one, otherwise the first
              card from the latest turn — the model surfaces exactly
              one card after a "Send X" intent per MS268). Single-
              click bridge from "Send this one" to the order form.

              MS66 directory-tool fallback ("Anything else?" when no
              cards exist) is preserved for the case where status:
              "completed" came from list_team_members or similar. */}
          {status === "completed" && (() => {
            const lastAssistant = [...turns].reverse().find(
              (t) => t.role === "assistant" && t.cards && t.cards.length > 0,
            );
            const sendableCard = lastAssistant?.cards?.[0] ?? null;
            if (sendableCard) {
              return (
                <div
                  className="flex flex-col gap-3 rounded-2xl border border-navy/15 bg-butter/30 px-4 py-3"
                  style={{ fontFamily: "DM Sans, sans-serif" }}
                  role="status"
                >
                  <p className="text-sm font-semibold text-navy">
                    Ready when you are.
                  </p>
                  {/* MS290: button label is just "Send this gift →" —
                      the SKU name lives on the card directly above,
                      so repeating it in the button caused 70+ char
                      buttons for long product names (e.g. Maker &
                      Monger's Loire Valley hamper). Short, unambiguous
                      label keeps the button readable on every viewport. */}
                  <button
                    type="button"
                    onClick={() => checkout.handleViewCard(sendableCard)}
                    className="inline-flex items-center justify-center gap-1.5 self-start rounded-full bg-coral px-5 py-2 text-sm font-semibold text-white shadow-[0_4px_12px_-4px_rgba(196,86,74,0.5)] transition-all hover:-translate-y-px hover:bg-coral/90 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
                  >
                    Send this gift
                    <ArrowRight size={14} strokeWidth={2.5} />
                  </button>
                </div>
              );
            }
            // MS66 preserved: directory-tool / no-cards "completed" branch.
            return (
              <div
                className="flex flex-col gap-2 rounded-2xl border border-navy/15 bg-butter/30 px-4 py-3 text-sm text-navy"
                style={{ fontFamily: "DM Sans, sans-serif" }}
                role="status"
              >
                <p className="font-semibold">Anything else?</p>
                <p>Ask another question, or tell me what to look at next.</p>
              </div>
            );
          })()}

          <div ref={threadEndRef} />
      </div>
    </>
  );

  return (
    <div
      className="mx-auto flex h-full w-full min-h-0 flex-col"
      style={{
        maxWidth: isMessenger ? "100%" : 640,
        textAlign: "left",
      }}
    >
      {!isMessenger && (
      <div
        className="mb-3 flex items-center justify-between gap-2"
        style={{ fontFamily: "DM Sans, sans-serif" }}
      >
        <button
          type="button"
          onClick={handleResetClick}
          className="inline-flex shrink-0 items-center gap-1.5 text-[12px] text-navy/55 transition-colors hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 rounded-full px-2 py-1"
          aria-label="Start over with a new brief"
        >
          <ArrowLeft size={12} strokeWidth={2.5} />
          Start over
        </button>
        <Link
          to="/browse"
          onClick={() => {
            void captureInteraction({
              surface: "landing",
              event_type: "browse_escape_clicked",
              payload: { placement: "inline_concierge_header" },
              recipient_id: initialBrief.recipient_id ?? null,
            });
          }}
          className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-navy/70 underline decoration-transparent underline-offset-4 transition-colors hover:text-coral hover:decoration-coral focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 rounded-full px-2 py-1"
        >
          Browse catalogue
          <ArrowRight size={11} strokeWidth={2.5} aria-hidden />
        </Link>
      </div>
      )}

      {isMessenger ? (
        <>
          <div
            ref={messageListRef}
            data-testid="concierge-message-list"
            onScroll={handleMessageListScroll}
            className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto"
            style={{
              WebkitOverflowScrolling: "touch",
              overflowAnchor: "auto",
            }}
          >
            {messageThread}
          </div>
          {composerSection}
        </>
      ) : (
      <div
        className="flex min-h-0 flex-1 flex-col rounded-2xl border border-charcoal/8 px-4 py-4 sm:px-5 sm:py-5"
        style={{ background: "rgba(255,255,255,0.62)" }}
      >
        <div
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto pr-1"
          style={{
            maxHeight: "min(65dvh, 560px)",
            WebkitOverflowScrolling: "touch",
            overflowAnchor: "auto",
          }}
        >
          {messageThread}
        </div>
        {composerSection}
      </div>
      )}

      {/* See-all overlay (full-screen modal — same as /chat) */}
      {overlayOpen && (
        <VendorGridOverlay
          title={
            initialBrief.recipient_name
              ? `${initialBrief.recipient_name} · all options`
              : "All options"
          }
          skus={overlaySkus}
          onClose={() => setOverlayOpen(false)}
          onView={(sku: SkuCardData) => {
            setOverlayOpen(false);
            checkout.handleViewCard(sku);
          }}
        />
      )}

      <ProductCheckoutModals
        flow={checkout}
        recipientName={initialBrief.recipient_name ?? ""}
        recipientId={initialBrief.recipient_id ?? null}
        occasion={initialBrief.occasion ?? "other"}
        defaultDate={checkoutDefaultDate}
        defaultAddress={checkoutDefaultAddress}
        isAnonymous={isAnon}
        guestSessionId={guestSessionId}
        chatSessionId={sessionId}
        celebrationId={celebrationId}
        onSubmitted={() => {
          void captureInteraction({
            surface: cameFrom === "dashboard" ? "dashboard" : "landing",
            event_type: "order_submitted",
            payload: {
              session_id: sessionId,
              sku_id: checkout.confirmingSku?.sku_id,
            },
            recipient_id: initialBrief.recipient_id ?? null,
          });
        }}
      />
    </div>
  );
}
